const PREFIX = 'iptv:';

export const storage = {
    get(key, fallback = null) {
        try {
            const raw = localStorage.getItem(PREFIX + key);
            return raw === null ? fallback : JSON.parse(raw);
        } catch (e) {
            return fallback;
        }
    },

    set(key, value) {
        try {
            localStorage.setItem(PREFIX + key, JSON.stringify(value));
            return true;
        } catch (e) {
            console.warn('[storage] set failed', key, e);
            return false;
        }
    },

    remove(key) {
        try {
            localStorage.removeItem(PREFIX + key);
        } catch (e) { /* ignore */ }
    },

    keys() {
        const out = [];
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.indexOf(PREFIX) === 0) out.push(k.slice(PREFIX.length));
            }
        } catch (e) { /* ignore */ }
        return out;
    }
};

const DEFAULT_SETTINGS = {
    lang: 'auto',
    introAnim: true, // animación del logo al abrir (apps: siempre; web: una vez por sesión, al entrar por la portada)
    coach: true, // ayudas en pantalla (una burbuja por pantalla, una sola vez)
    introSound: true, // con su sonido
    tvMode: 'auto',
    fireLayout: false, // Ajustes y «Añadir fuente» con el aspecto del Fire TV también en el móvil y el PC (en la tele siempre)
    uiScale: 1,
    navFade: 33, // % de transparencia del menú superior sobre la cabecera de Inicio (0 = barra oscura, 100 = totalmente transparente)
    skin: 'default', // color de acento (ver src/core/skins.js)
    theme: 'dark', // dark | light | auto (según el sistema)
    autoplayNext: true,
    liveQuality: 'auto', // variantes SD/HD/FHD del mismo canal: auto (según la conexión) | best | saver
    autoplayPreview: true,
    viewerAutoPick: 6, // segundos que espera la pantalla «¿Quién está viendo?» antes de entrar sola con el último espectador (0 = no)
    similarByTaste: true, // «Más como esto» de la ficha: por parecido y gustos del espectador (si no, la categoría tal cual)
    continueChannels: true, // los canales vistos hace poco también salen en «Continuar viendo» del Inicio
    heroInterval: 8, // segundos entre destacados del carrusel (0 = no cambia solo)
    catalogAuto: true, // actualizar el catálogo cada día, sin que nadie lo pida (con la app abierta)
    compactLiveRow: true, // «Continuar en directo» del Inicio con tarjetas algo más pequeñas
    homeFilters: true, // el Inicio respeta los filtros elegidos en TV en directo, Películas y Series
    libraryBarOverlay: false, // la barra de Películas/Series va superpuesta a la cabecera destacada
    libraryHero: true, // cabecera destacada (carrusel) en Películas y en Series
    tmdbMode: 'auto', // fichas con TMDB: 'auto' (completa lo que falte) | 'basics' (solo si la biblioteca no trae lo básico) | 'omdb' (no se usa si hay OMDb configurado; sin OMDb, como 'auto') | 'off' (nunca)
    omdbOn: true, // web: valoraciones de OMDb (si el servidor tiene su clave)
    catalogAsked: '[]', // identificadores ya pedidos al catálogo desde este aparato (para no repetir)
    catalogOn: true, // completar las fichas con el catálogo de Filoteca (datos de Wikidata, CC0), por identificador exacto
    cardsPosterOnly: true, // tarjetas de películas y series solo con la carátula (sin título ni nota debajo); se puede desactivar en Ajustes → Aspecto
    peekCard: true, // tarjeta ampliada al dejar el ratón o el foco sobre una carátula
    peekStyle: 'row', // dónde se amplía en Películas y Series (filas por categorías): 'row' crece dentro de la fila (como Prime Video), 'float' sale flotando encima con tráiler y sinopsis
    episodeView: 'auto', // auto (tarjetas en pantallas grandes) | cards | list
    detailHeader: 'summary', // summary | full: qué se ve bajo el título de la ficha
    trailerButton: false, // botón «Tráiler» en la cabecera de la ficha
    seekPreview: true,
    relayTyping: true,
    liveOkAction: 'channels', // OK en el directo con los controles ocultos (tele): channels (lista de canales, sigue sonando) | pause
    uiSounds: 'auto', // sonidos de navegación: auto (solo en la tele) | on | off
    rememberPosition: true,
    channelListStyle: 'strip', // lista de canales en el reproductor: strip (tira inferior con el directo de fondo; por defecto) | panel (lateral)
    liveInfoStyle: 'guide', // «i» del directo: guide (programa en emisión, siguientes, ver desde el inicio…) | simple
    prefetchFocus: true,
    playerBeta: false, // reproductor nativo «beta»: precarga de verdad del canal enfocado (Media3 PreloadManager) // preparar el canal (pedir su lista HLS) al pararse sobre él con el ratón o el mando

    shell: 'cine', // aspecto completo de la interfaz (ver src/core/shells.js); «cine» es el diseño original
    liveFormat: 'm3u8',
    nextOfferSec: 180,
    transcodeAudio: 'auto', // auto | always | never: convertir audio Dolby/DTS en el servidor
    subtitlesAuto: false, // buscar y cargar subtítulos al empezar cada película o episodio
    subtitleLang: 'es', // idioma de los subtítulos que se buscan (código de OpenSubtitles)
    proxyUrl: '',
    serverUrl: '',
    liveView: 'rows', // grid | rows (por categorías, por defecto) | list | stage
    liveSort: 'default', // default (orden del proveedor) | name
    libraryView: 'rows', // películas y series: grid | rows (por categorías, por defecto) | list | stage
    moviesSort: 'added',
    seriesSort: 'added',
    parental: {
        // Activado por defecto: cualquier categoría que parezca de contenido adulto queda
        // bloqueada hasta que el usuario la desbloquee (y, si quiere, configure un PIN en Ajustes).
        enabled: true,
        pinHash: '',
        categories: {}
    }
};

export const settings = {
    _data: null,

    load() {
        const saved = storage.get('settings', {}) || {};
        // `set` guarda todos los ajustes (también los valores por defecto de entonces): quien cambió cualquier ajuste tenía guardado
        // «panel» como lista de canales aunque nunca lo eligiera. La tira inferior pasó a ser la de por defecto: se aplica una sola vez.
        if (saved.channelListStyle === 'panel' && !saved.channelListStyleMigrated) {
            saved.channelListStyle = 'strip';
            saved.channelListStyleMigrated = true;
            storage.set('settings', saved);
        }
        this._data = Object.assign({}, DEFAULT_SETTINGS, saved, {
            parental: Object.assign({}, DEFAULT_SETTINGS.parental, saved.parental || {})
        });
        return this._data;
    },

    get(key) {
        if (!this._data) this.load();
        return this._data[key];
    },

    /** Los valores por defecto de los ajustes que los tienen declarados (copia). */
    defaults() {
        return JSON.parse(JSON.stringify(DEFAULT_SETTINGS));
    },

    set(key, value) {
        if (!this._data) this.load();
        this._data[key] = value;
        storage.set('settings', this._data);
        return value;
    },

    all() {
        if (!this._data) this.load();
        return this._data;
    }
};
